import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";
const CARD_BG = "#F6F4EF";

export default function StoryPage() {
  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Editorial masthead */}
      <section style={{ padding: "120px 40px 24px", maxWidth: 1280, margin: "0 auto" }}>
        <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 20,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (00)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span style={{ fontSize: 10.5, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
            THE STORY · A LETTER FROM MILANO
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, filter: "blur(12px)", y: 12 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
            fontWeight: 600,
            fontSize: 68,
            lineHeight: 1,
            letterSpacing: "-1.6px",
            color: TEXT_COLOR,
            margin: 0,
          }}
        >
          A small room,{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-2.8px",
            }}
          >
            a basket of yarn,
          </span>
          <br />
          and six pairs of{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-2.8px",
            }}
          >
            patient hands.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          style={{
            maxWidth: 540,
            fontSize: 15,
            lineHeight: 1.8,
            color: "rgba(84,84,84,0.72)",
            marginTop: 22,
          }}
        >
          Maison des Jouets is a very small atelier on the second floor of an old
          Milanese building. We make soft, honest companions — one at a time, and
          never in a hurry. This is how we got here.
        </motion.p>
      </section>

      {/* Chapter I — Beginnings */}
      <section
        style={{
          padding: "72px 40px 20px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader index="(I)" label="BEGINNINGS · 2019" title={
          <>
            It started with{" "}
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              a rabbit
            </span>
            .
          </>
        } />

        <div
          className="grid"
          style={{ gridTemplateColumns: "1fr 1.15fr", gap: 56, marginTop: 40, alignItems: "start" }}
        >
          <div>
            <p style={paragraphStyle}>
              In the winter of 2019, Elena — our founder — stitched a small linen rabbit
              for her nephew's second birthday. It had one crooked ear, a slightly lopsided
              smile, and a very serious expression. The nephew loved it. His mother asked
              for two more. Then a friend asked. Then a stranger.
            </p>
            <p style={{ ...paragraphStyle, marginTop: 18 }}>
              By spring, the kitchen table wasn't enough. We rented{" "}
              <em style={emStyle}>a single room</em> above a stationery shop on Via Solari,
              hung the yarn on the walls, and gave it a name.
            </p>
            <div style={{ marginTop: 26 }}>
              <PullQuote>
                &ldquo;We wanted to make things that were{" "}
                <em style={emStyle}>a little imperfect</em>,&mdash; the kind that get
                loved into shape over years, not months.&rdquo;
              </PullQuote>
              <div style={{ marginTop: 14, fontSize: 11, letterSpacing: "2px", color: "rgba(84,84,84,0.5)" }}>
                — ELENA MARCHETTI, FOUNDER
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "5 / 6",
                background: CARD_BG,
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src="https://images.pexels.com/photos/6045240/pexels-photo-6045240.jpeg?auto=compress&cs=tinysrgb&w=1000"
                alt="Handmade amigurumi companions on a wooden shelf"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 14,
                  left: 16,
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 16,
                  color: "#FFFFFF",
                  textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                }}
              >
                fig. 01 — the room above the stationery shop
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chapter II — The Makers */}
      <section
        style={{
          padding: "80px 40px 20px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader
          index="(II)"
          label="THE MAKERS · SIX PAIRS OF HANDS"
          title={
            <>
              The{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                small
              </span>{" "}
              team.
            </>
          }
        />

        <p style={{ ...paragraphStyle, maxWidth: 640, marginTop: 30, marginBottom: 40 }}>
          There are six of us. We drink too much coffee, keep the radio quiet, and none of
          us are in a rush. Everyone signs the pouch of whatever they make.
        </p>

        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 40 }}
        >
          {MAKERS.map((m, i) => (
            <MakerCard key={m.name} maker={m} index={i} />
          ))}
        </div>
      </section>

      {/* Chapter III — The Slow Way */}
      <section
        style={{
          padding: "80px 40px 20px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader
          index="(III)"
          label="THE METHOD · SLOW ON PURPOSE"
          title={
            <>
              How a{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                small thing
              </span>{" "}
              gets made.
            </>
          }
        />

        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 32, marginTop: 44 }}
        >
          {METHOD_STEPS.map((s, i) => (
            <MethodStep key={s.title} step={s} index={i} />
          ))}
        </div>

        <div
          style={{
            marginTop: 60,
            padding: "28px 34px",
            border: `1px solid ${BORDER_SOFT}`,
            borderRadius: 4,
            background: "#FBFAF7",
          }}
          className="flex items-center justify-between flex-wrap"
        >
          <div style={{ maxWidth: 620 }}>
            <div
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 26,
                lineHeight: 1.15,
                letterSpacing: "-0.5px",
              }}
            >
              On average, one companion takes{" "}
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontStyle: "normal", fontWeight: 500, fontSize: 22 }}>
                4 hours & 40 minutes
              </span>{" "}
              of quiet work.
            </div>
            <p style={{ ...paragraphStyle, fontSize: 13.5, marginTop: 10 }}>
              We batch six or seven at a time — never more. When they're done, they rest on
              a shelf for a night before we tie the ribbon.
            </p>
          </div>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 60,
              color: "rgba(84,84,84,0.15)",
              lineHeight: 1,
            }}
          >
            04:40
          </span>
        </div>
      </section>

      {/* Chapter IV — Materials */}
      <section
        style={{
          padding: "80px 40px 20px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader
          index="(IV)"
          label="THE MATERIALS · WHERE THINGS COME FROM"
          title={
            <>
              Everything has{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                a place
              </span>{" "}
              it comes from.
            </>
          }
        />

        <div
          className="grid"
          style={{ gridTemplateColumns: "1.1fr 1fr", gap: 56, marginTop: 40, alignItems: "start" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "5 / 4",
                background: CARD_BG,
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src="https://images.pexels.com/photos/6045232/pexels-photo-6045232.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Cotton yarn and a half-finished handmade doll"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </div>
          </motion.div>

          <div>
            {MATERIALS.map((m, i) => (
              <div
                key={m.title}
                style={{
                  padding: "22px 0",
                  borderBottom: i === MATERIALS.length - 1 ? "none" : `1px dashed ${BORDER_MID}`,
                }}
              >
                <div className="flex items-baseline" style={{ gap: 14 }}>
                  <span
                    style={{
                      fontFamily: "'Instrument Serif', serif",
                      fontStyle: "italic",
                      fontSize: 22,
                      color: "rgba(84,84,84,0.5)",
                      minWidth: 34,
                    }}
                  >
                    {m.mark}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 22,
                        letterSpacing: "-0.4px",
                        lineHeight: 1.2,
                      }}
                    >
                      {m.title}
                    </div>
                    <p style={{ ...paragraphStyle, fontSize: 13, marginTop: 6 }}>{m.body}</p>
                    <div
                      style={{
                        fontSize: 10.5,
                        letterSpacing: "2px",
                        color: "rgba(84,84,84,0.55)",
                        marginTop: 8,
                        textTransform: "uppercase",
                      }}
                    >
                      {m.origin}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chapter V — Values */}
      <section
        style={{
          padding: "80px 40px 20px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader
          index="(V)"
          label="THE PROMISES · FIVE QUIET RULES"
          title={
            <>
              What we{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                promise,
              </span>{" "}
              in short.
            </>
          }
        />

        <div style={{ marginTop: 44 }}>
          {PROMISES.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: "easeOut" }}
              style={{
                display: "grid",
                gridTemplateColumns: "80px 1fr 2fr",
                gap: 32,
                padding: "26px 0",
                borderTop: i === 0 ? `1px solid ${BORDER_SOFT}` : "none",
                borderBottom: `1px solid ${BORDER_SOFT}`,
                alignItems: "baseline",
              }}
            >
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 24,
                  color: "rgba(84,84,84,0.5)",
                }}
              >
                {p.number}
              </span>
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 30,
                  letterSpacing: "-0.6px",
                  lineHeight: 1.15,
                }}
              >
                {p.title}
              </div>
              <p style={{ ...paragraphStyle, fontSize: 13.5 }}>{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Chapter VI — Numbers */}
      <section
        style={{
          padding: "80px 40px 40px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <ChapterHeader
          index="(VI)"
          label="THE NUMBERS · A SMALL LEDGER"
          title={
            <>
              In{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                seven years,
              </span>{" "}
              roughly.
            </>
          }
        />

        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 24, marginTop: 44 }}
        >
          {LEDGER.map((n) => (
            <div
              key={n.label}
              style={{
                borderTop: `1px solid ${BORDER_MID}`,
                paddingTop: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 60,
                  lineHeight: 1,
                  letterSpacing: "-2px",
                  color: TEXT_COLOR,
                }}
              >
                {n.value}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: "2px",
                  color: "rgba(84,84,84,0.6)",
                  marginTop: 12,
                  textTransform: "uppercase",
                }}
              >
                {n.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing letter + CTA */}
      <section
        style={{
          padding: "80px 40px 60px",
          maxWidth: 1280,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}
        >
          <div>
            <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 20,
                  color: "rgba(84,84,84,0.55)",
                }}
              >
                (07)
              </span>
              <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
              <span style={{ fontSize: 10.5, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
                A NOTE TO CLOSE
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
                fontWeight: 600,
                fontSize: 48,
                lineHeight: 1.05,
                letterSpacing: "-1.2px",
                margin: 0,
              }}
            >
              Thank you for{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                reading this far.
              </span>
            </h2>
          </div>

          <div>
            <p style={{ ...paragraphStyle, fontSize: 15 }}>
              If any of this sounds like your kind of thing, come have a look at the
              collection. Everything you'll find there was made in the small room, by
              someone whose name is written on the pouch.
            </p>
            <p style={{ ...paragraphStyle, fontSize: 15, marginTop: 16 }}>
              — with love, <em style={emStyle}>from Milano</em>.
            </p>

            <div className="flex items-center flex-wrap" style={{ gap: 14, marginTop: 32 }}>
              <Link
                to="/catalog"
                className="cursor-pointer whitespace-nowrap inline-flex items-center"
                style={{
                  gap: 10,
                  background: TEXT_COLOR,
                  color: "#FFFFFF",
                  border: 0,
                  borderRadius: 999,
                  padding: "13px 26px",
                  fontSize: 11,
                  letterSpacing: "2.2px",
                  textDecoration: "none",
                }}
              >
                MEET THE COLLECTION
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 15, letterSpacing: 0 }}>
                  →
                </span>
              </Link>
              <a
                href="mailto:hello@maisondesjouets.com"
                className="cursor-pointer whitespace-nowrap inline-flex items-center"
                style={{
                  gap: 10,
                  background: "transparent",
                  color: TEXT_COLOR,
                  border: `1px solid ${BORDER_MID}`,
                  borderRadius: 999,
                  padding: "13px 24px",
                  fontSize: 11,
                  letterSpacing: "2.2px",
                  textDecoration: "none",
                }}
              >
                WRITE TO THE ATELIER
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial signature */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 26,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)" }}>
          MAISON DES JOUETS · CHAPTER I—VII
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — end of the story, for now
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}

/* ---------- shared styles ---------- */
const paragraphStyle: React.CSSProperties = {
  fontSize: 14.5,
  lineHeight: 1.8,
  color: "rgba(84,84,84,0.78)",
  margin: 0,
};

const emStyle: React.CSSProperties = {
  fontFamily: "'Instrument Serif', serif",
  fontStyle: "italic",
  fontWeight: 400,
  color: TEXT_COLOR,
};

/* ---------- sub-components ---------- */
function ChapterHeader({
  index,
  label,
  title,
}: {
  index: string;
  label: string;
  title: React.ReactNode;
}) {
  return (
    <>
      <div className="flex items-center" style={{ gap: 14, marginBottom: 22, paddingTop: 40 }}>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 20,
            color: "rgba(84,84,84,0.55)",
          }}
        >
          {index}
        </span>
        <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
        <span style={{ fontSize: 10.5, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
          {label}
        </span>
      </div>
      <h2
        style={{
          fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
          fontWeight: 600,
          fontSize: 52,
          lineHeight: 1.05,
          letterSpacing: "-1.2px",
          margin: 0,
        }}
      >
        {title}
      </h2>
    </>
  );
}

function PullQuote({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        borderLeft: `2px solid ${BORDER_MID}`,
        paddingLeft: 20,
        fontFamily: "'Instrument Serif', serif",
        fontStyle: "italic",
        fontSize: 26,
        lineHeight: 1.35,
        letterSpacing: "-0.4px",
        color: TEXT_COLOR,
      }}
    >
      {children}
    </div>
  );
}

interface Maker {
  name: string;
  role: string;
  joined: string;
  since: string;
  quote: string;
  image: string;
}

const MAKERS: Maker[] = [
  {
    name: "Elena",
    role: "founder & hand-stitcher",
    joined: "founded, 2019",
    since: "07 years",
    quote: "I still make the rabbits. That's my rule.",
    image:
      "https://images.pexels.com/photos/6045082/pexels-photo-6045082.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    name: "Matteo",
    role: "wood & wooden faces",
    joined: "joined, 2020",
    since: "06 years",
    quote: "A face should look like it's already thinking about something.",
    image:
      "https://images.pexels.com/photos/6045245/pexels-photo-6045245.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    name: "Sofia",
    role: "plant-dye & yarn",
    joined: "joined, 2021",
    since: "05 years",
    quote: "Colour is a slow conversation, not a decision.",
    image:
      "https://images.pexels.com/photos/6045248/pexels-photo-6045248.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    name: "Giulia",
    role: "linen & embroidery",
    joined: "joined, 2022",
    since: "04 years",
    quote: "Every stitch is a very small opinion.",
    image:
      "https://images.pexels.com/photos/6045085/pexels-photo-6045085.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    name: "Luca",
    role: "packaging & letters",
    joined: "joined, 2023",
    since: "03 years",
    quote: "The pouch is part of the toy. Same care, same signature.",
    image:
      "https://images.pexels.com/photos/6045238/pexels-photo-6045238.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    name: "Chiara",
    role: "atelier & the radio",
    joined: "joined, 2024",
    since: "02 years",
    quote: "Someone has to remember to water the fig tree.",
    image:
      "https://images.pexels.com/photos/6045230/pexels-photo-6045230.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
];

function MakerCard({ maker, index }: { maker: Maker; index: number }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: "easeOut" }}
      style={{ position: "relative" }}
    >
      <div
        style={{
          position: "absolute",
          top: -14,
          left: 2,
          zIndex: 3,
          fontFamily: "'Instrument Serif', serif",
          fontStyle: "italic",
          fontSize: 18,
          color: "rgba(84,84,84,0.5)",
          pointerEvents: "none",
        }}
      >
        ({num})
      </div>
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 5",
          overflow: "hidden",
          background: CARD_BG,
          borderRadius: 4,
        }}
      >
        <img
          src={maker.image}
          alt={maker.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
          }}
        />
      </div>
      <div style={{ padding: "16px 2px 0" }}>
        <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 30,
              lineHeight: 1,
              letterSpacing: "-0.6px",
            }}
          >
            {maker.name}
          </div>
          <div
            style={{
              fontSize: 10.5,
              letterSpacing: "2px",
              color: "rgba(84,84,84,0.55)",
              textTransform: "uppercase",
            }}
          >
            {maker.since}
          </div>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "rgba(84,84,84,0.7)",
            marginTop: 6,
            fontStyle: "italic",
            fontFamily: "'Instrument Serif', serif",
          }}
        >
          — {maker.role}
        </div>
        <div
          style={{
            fontSize: 10.5,
            letterSpacing: "2px",
            color: "rgba(84,84,84,0.5)",
            marginTop: 4,
            textTransform: "uppercase",
          }}
        >
          {maker.joined}
        </div>
        <p
          style={{
            marginTop: 14,
            fontSize: 13,
            lineHeight: 1.7,
            color: "rgba(84,84,84,0.78)",
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
          }}
        >
          &ldquo;{maker.quote}&rdquo;
        </p>
      </div>
    </motion.div>
  );
}

interface Step {
  greek: string;
  title: string;
  body: string;
  duration: string;
}

const METHOD_STEPS: Step[] = [
  {
    greek: "(α)",
    title: "the sketch",
    body:
      "Every companion starts as a small pencil sketch pinned to a corkboard. Nothing is drawn on a screen.",
    duration: "day one, morning",
  },
  {
    greek: "(β)",
    title: "the cut",
    body:
      "Linen is cut by hand from a single bolt. We keep every offcut and turn them into little pouches.",
    duration: "day one, afternoon",
  },
  {
    greek: "(γ)",
    title: "the stitch",
    body:
      "Stitched by one person, start to finish. That person's initials go on the pouch — always.",
    duration: "days two & three",
  },
  {
    greek: "(δ)",
    title: "the rest",
    body:
      "A finished companion sits on a shelf overnight. In the morning, we tie the ribbon and write the card.",
    duration: "day four",
  },
];

function MethodStep({ step, index }: { step: Step; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: "easeOut" }}
      style={{
        borderTop: `1px solid ${BORDER_MID}`,
        paddingTop: 18,
      }}
    >
      <div
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontStyle: "italic",
          fontSize: 30,
          color: "rgba(84,84,84,0.6)",
          lineHeight: 1,
        }}
      >
        {step.greek}
      </div>
      <div
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontStyle: "italic",
          fontSize: 26,
          letterSpacing: "-0.5px",
          marginTop: 12,
          lineHeight: 1.1,
        }}
      >
        {step.title}
      </div>
      <p style={{ ...paragraphStyle, fontSize: 13, marginTop: 12 }}>{step.body}</p>
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: "2px",
          color: "rgba(84,84,84,0.55)",
          marginTop: 14,
          textTransform: "uppercase",
        }}
      >
        {step.duration}
      </div>
    </motion.div>
  );
}

interface Material {
  mark: string;
  title: string;
  body: string;
  origin: string;
}

const MATERIALS: Material[] = [
  {
    mark: "i.",
    title: "European organic cotton",
    body: "Woven in a small mill outside Como. We use only three weights, all undyed at the loom.",
    origin: "COMO, ITALY · GOTS-CERTIFIED",
  },
  {
    mark: "ii.",
    title: "Belgian linen",
    body: "Rough, honest, and gets softer every year. Cut by hand — we don't own a laser cutter.",
    origin: "COURTRAI, BELGIUM",
  },
  {
    mark: "iii.",
    title: "Plant-based dye",
    body: "Madder root, walnut hull, chamomile, sometimes a little onion skin. Everything you can eat.",
    origin: "PIEMONTE, ITALY · SEASONAL BATCHES",
  },
  {
    mark: "iv.",
    title: "European beech wood",
    body: "For eyes, buttons and small joints. Turned on a lathe that has been in Matteo's family since 1968.",
    origin: "TIROL, AUSTRIA · FSC-CERTIFIED",
  },
];

interface Promise {
  number: string;
  title: string;
  body: string;
}

const PROMISES: Promise[] = [
  {
    number: "(01)",
    title: "One thing at a time",
    body:
      "No factories, no overseas production, no white-label. Every piece is made in the same room by someone whose name is on the pouch.",
  },
  {
    number: "(02)",
    title: "Materials that tell the truth",
    body:
      "Only natural fibres, plant-based dyes, and wood from responsibly managed European forests. Nothing synthetic, nothing that pretends to be what it isn't.",
  },
  {
    number: "(03)",
    title: "Repair, always",
    body:
      "If something needs fixing, we fix it. Send it back to the atelier, in any state, at any age. We mend for free — you cover the postage.",
  },
  {
    number: "(04)",
    title: "Paid, fairly & fully",
    body:
      "Everyone on the team is paid a Milano living wage, gets full holiday, and shares 10% of yearly profit at year's end. No one is a freelancer.",
  },
  {
    number: "(05)",
    title: "Slow, on purpose",
    body:
      "We batch six or seven pieces at a time. When the shelf is empty, it stays empty until the next batch is ready. We'd rather wait than rush.",
  },
];

const LEDGER = [
  { value: "07", label: "YEARS IN THE ATELIER" },
  { value: "06", label: "HANDS ON THE TEAM" },
  { value: "3,240", label: "COMPANIONS MADE, ROUGHLY" },
  { value: "1", label: "FIG TREE, STILL ALIVE" },
];